import { useReducedMotion } from "@/hooks/useReducedMotion";
import { cn } from "@/lib/utils";
import { appleSprings } from "@/ui/tokens/apple-springs";
import { motion } from "framer-motion";
import React, { useCallback, useMemo, useRef } from "react";

/**
 * LiquidGlassCard Canonical Liquid Glass Surface (iOS 26 Style)
 *
 * Implements Apple's Liquid Glass design language with:
 * - Canonical material formula: 12px blur + 180% saturation
 * - Surface tier opacity: 70-85% gradient
 * - Specular highlight (top edge reflection)
 * - Inset shadow for depth
 * - Dark mode material shift
 * - Reduced motion support
 *
 * @see CANON.md Section 5.4b Liquid Glass System
 */

interface LiquidGlassCardProps {
  className?: string;
  children: React.ReactNode;
  /** Enable hover scale and shimmer effects */
  interactive?: boolean;
  /** Glass opacity tier: 'chrome' (85-95%), 'surface' (70-85%), 'ambient' (40-60%) */
  tier?: 'chrome' | 'surface' | 'ambient';
  /** Hover interaction style */
  hoverEffect?: 'scale' | 'lift' | 'glow' | 'none';
  /** Click handler */
  onClick?: () => void;
  /** Accessibility: pressed state for toggle-like cards */
  'aria-pressed'?: boolean;
  /** Accessibility: label for screen readers */
  'aria-label'?: string;
}

// Canonical opacity tiers (Liquid Glass Fintech Standard)
// FIXED: Using visible borders (slate-based) instead of invisible white borders
const TIER_CLASSES = {
  chrome: {
    light: 'from-white/95 via-white/88 to-white/95',
    dark: 'dark:from-slate-900/85 dark:via-slate-800/75 dark:to-slate-900/85',
    border: 'border-slate-200/60 dark:border-white/12',
  },
  surface: {
    light: 'from-white/90 via-white/75 to-white/90',
    // Dark theme: More opaque to prevent background gradient from showing through
    dark: 'dark:from-slate-900/90 dark:via-slate-800/85 dark:to-slate-900/90',
    border: 'border-slate-200/50 dark:border-white/10',
  },
  ambient: {
    light: 'from-white/70 via-white/55 to-white/70',
    dark: 'dark:from-slate-900/55 dark:via-slate-800/40 dark:to-slate-900/55',
    border: 'border-slate-200/40 dark:border-white/8',
  },
} as const;

const LiquidGlassCard: React.FC<LiquidGlassCardProps> = ({
  className = "",
  children,
  interactive = true,
  tier = 'surface',
  hoverEffect = 'scale',
  onClick,
  'aria-pressed': ariaPressed,
  'aria-label': ariaLabel,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();
  const tierStyles = TIER_CLASSES[tier];

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!interactive || !cardRef.current || prefersReducedMotion) return;

    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    // Use requestAnimationFrame for smooth updates
    requestAnimationFrame(() => {
      if (cardRef.current) {
        cardRef.current.style.setProperty('--mouse-x', `${x}%`);
        cardRef.current.style.setProperty('--mouse-y', `${y}%`);
      }
    });
  }, [interactive, prefersReducedMotion]);

  // Apple-style spring physics based on hover effect
  const motionProps = useMemo(() => {
    if (prefersReducedMotion || !interactive || hoverEffect === 'none') return {};

    if (hoverEffect === 'scale') {
      return {
        whileHover: { scale: 1.02 },
        whileTap: { scale: 0.98 },
        transition: appleSprings.scale
      };
    }

    if (hoverEffect === 'lift') {
      return {
        whileHover: { y: -4 },
        whileTap: { y: -2 },
        transition: appleSprings.standard
      };
    }

    // 'glow' effect only handles shadow/border via CSS, no transform
    return {};
  }, [prefersReducedMotion, interactive, hoverEffect]);

  // Hover shadow classes based on effect
  const getHoverClasses = () => {
    if (prefersReducedMotion || !interactive) return "";

    if (hoverEffect === 'scale') {
      return "hover:shadow-xl dark:hover:shadow-none dark:hover:shadow-drop-xl";
    }

    if (hoverEffect === 'lift') {
      return "hover:shadow-2xl dark:hover:shadow-none dark:hover:shadow-drop-2xl";
    }

    if (hoverEffect === 'glow') {
      return "hover:shadow-xl hover:shadow-primary/10 hover:border-primary/30 dark:hover:shadow-none dark:hover:shadow-drop-xl-primary dark:hover:border-primary/40";
    }

    return "";
  };

  return (
    <motion.div
      ref={cardRef}
      className={cn(
        "group relative rounded-2xl sm:rounded-3xl",
        // Ensure overflow-hidden so inner layer border follows rounded corners perfectly
        "overflow-hidden",
        // Canonical shadow from theme
        // Use filter: drop-shadow() in dark mode to fix sharp corners in shadows
        // drop-shadow follows border-radius better than box-shadow
        "shadow-lg",
        "dark:shadow-none dark:shadow-drop-xl",
        // Hover effect classes
        getHoverClasses(),
        // CANON: 240ms canonical motion duration
        "transition-all duration-240 ease-standard",
        onClick && "cursor-pointer",
        className
      )}
      onMouseMove={handleMouseMove}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-pressed={ariaPressed}
      aria-label={ariaLabel}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      } : undefined}
      {...motionProps}
    >
      {/* Glass surface with canonical material formula */}
      <div
        className={cn(
          "relative rounded-2xl sm:rounded-3xl",
          // Ensure inner layer stretches to full extent (100% width and height)
          "h-full w-full min-h-full",
          // Canonical gradient (tier-based opacity)
          "bg-gradient-to-br",
          tierStyles.light,
          tierStyles.dark,
          // Canonical blur + saturation (12px blur, 180% saturation)
          "backdrop-blur-lg backdrop-saturate-[180%]",
          "dark:backdrop-blur-lg dark:backdrop-saturate-150 dark:brightness-90",
          // Canonical border (single, clean - NO ring to avoid double-border effect)
          // Border will follow rounded corners due to overflow-hidden on parent
          "border",
          tierStyles.border
        )}
      >
        {/* Specular highlight (top edge reflection) Apple Liquid Glass signature */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/20 z-20 rounded-t-2xl sm:rounded-t-3xl" />
        {/* Interactive shimmer effect (L2 Enhanced) */}
        {!prefersReducedMotion && interactive && (
          <div
            className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-240 rounded-2xl sm:rounded-3xl overflow-hidden"
            style={{
              background: `radial-gradient(circle at var(--mouse-x, 50%) var(--mouse-y, 50%),
                hsl(0 0% 100% / 0.15) 0%,
                transparent 50%)`
            }}
          />
        )}

        {/* Content */}
        <div className="relative z-10">
          {children}
        </div>
      </div>
    </motion.div>
  );
};

export default LiquidGlassCard;
